/**
 * Trips API Hooks
 * React hooks for trip management endpoints
 * Based on trips-controller from admin API
 * 
 * NOTE: Currently using MOCK DATA for demonstration purposes.
 * Remove the mock data and uncomment the actual API calls when backend is ready.
 */

import { useFetch, buildUrl, useMutation } from './useApi';
import { ADMIN_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';
import { useCallback } from 'react';
import type {
  Trip,
  TripStatus,
  TripFilterParams,
  TripLocation,
  TripReplayPoint,
  TripHistoryItem,
  ForceEndTripRequest,
  AssignDriverRequest,
  ExportTripsParams,
  TopRoute,
  CityPerformance,
  DriverPerformance,
  CancellationReason,
  SendReceiptRequest,
  TripsListResponse,
  TripResponse,
  TripLocationsResponse,
  TripReplayResponse,
  TripHistoryResponse,
  TopRoutesResponse,
  CityPerformanceResponse,
  DriverPerformanceResponse,
  CancellationReasonsResponse,
} from './tripsTypes';
import type { Pageable, PagedData, ApiResponse } from './types';

const API_BASE_URL_ADMIN = ADMIN_API_BASE_URL;

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_TRIPS: Trip[] = [
  {
    tripId: 1001,
    bookingId: 'BK-2025-001',
    riderId: 1,
    riderName: 'John Smith',
    riderPhone: '+1 555-0101',
    driverId: 5,
    driverName: 'Michael Johnson',
    driverPhone: '+1 555-0201',
    status: 'COMPLETED',
    pickupLocation: {
      latitude: 40.7128,
      longitude: -74.0060,
      address: '123 Broadway, New York, NY 10001'
    },
    dropoffLocation: {
      latitude: 40.7589,
      longitude: -73.9851,
      address: '456 Park Ave, New York, NY 10022'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 12.50,
      timeFare: 3.25,
      subtotal: 20.75,
      surgeMultiplier: 1.0,
      discount: 2.00,
      tax: 1.50,
      platformFee: 1.00,
      totalFare: 21.25
    },
    distance: 5.2,
    duration: 18,
    vehicleType: 'CAR',
    paymentMethod: 'CARD',
    paymentStatus: 'PAID',
    requestedAt: '2025-02-19T08:30:00Z',
    acceptedAt: '2025-02-19T08:32:00Z',
    arrivedAt: '2025-02-19T08:38:00Z',
    startedAt: '2025-02-19T08:40:00Z',
    completedAt: '2025-02-19T08:58:00Z',
    rating: 5,
    feedback: 'Great ride! Driver was very professional.',
    city: 'New York'
  },
  {
    tripId: 1002,
    bookingId: 'BK-2025-002',
    riderId: 2,
    riderName: 'Sarah Wilson',
    riderPhone: '+1 555-0102',
    driverId: 6,
    driverName: 'David Brown',
    driverPhone: '+1 555-0202',
    status: 'IN_PROGRESS',
    pickupLocation: {
      latitude: 40.7505,
      longitude: -73.9934,
      address: '789 Madison Ave, New York, NY 10065'
    },
    dropoffLocation: {
      latitude: 40.7282,
      longitude: -73.7949,
      address: '321 Queens Blvd, Queens, NY 11415'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 18.75,
      timeFare: 5.50,
      subtotal: 29.25,
      surgeMultiplier: 1.2,
      discount: 0,
      tax: 2.20,
      platformFee: 1.50,
      totalFare: 33.00
    },
    distance: 8.5,
    duration: 25,
    vehicleType: 'CAR',
    paymentMethod: 'WALLET',
    paymentStatus: 'PENDING',
    requestedAt: '2025-02-19T09:15:00Z',
    acceptedAt: '2025-02-19T09:17:00Z',
    arrivedAt: '2025-02-19T09:25:00Z',
    startedAt: '2025-02-19T09:28:00Z',
    city: 'New York'
  },
  {
    tripId: 1003,
    bookingId: 'BK-2025-003',
    riderId: 3,
    riderName: 'Emily Davis',
    riderPhone: '+1 555-0103',
    status: 'PENDING',
    pickupLocation: {
      latitude: 40.6892,
      longitude: -74.0445,
      address: '100 Wall St, New York, NY 10005'
    },
    dropoffLocation: {
      latitude: 40.7484,
      longitude: -73.9857,
      address: '350 5th Ave, New York, NY 10118'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 8.50,
      timeFare: 2.75,
      subtotal: 16.25,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 1.20,
      platformFee: 0.80,
      totalFare: 18.25
    },
    distance: 3.2,
    duration: 12,
    vehicleType: 'CAR',
    paymentMethod: 'CARD',
    paymentStatus: 'PENDING',
    requestedAt: '2025-02-19T09:45:00Z',
    city: 'New York'
  },
  {
    tripId: 1004,
    bookingId: 'BK-2025-004',
    riderId: 4,
    riderName: 'Robert Taylor',
    riderPhone: '+1 555-0104',
    driverId: 7,
    driverName: 'James Anderson',
    driverPhone: '+1 555-0203',
    status: 'CANCELLED',
    cancellationReason: 'LONG_WAIT_TIME',
    cancelledBy: 'RIDER',
    pickupLocation: {
      latitude: 40.7614,
      longitude: -73.9776,
      address: '10 Columbus Circle, New York, NY 10019'
    },
    dropoffLocation: {
      latitude: 40.8229,
      longitude: -73.9442,
      address: 'Harlem, New York, NY 10026'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 0,
      timeFare: 0,
      subtotal: 5.00,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 0,
      platformFee: 0,
      totalFare: 0,
      cancellationFee: 2.50
    },
    distance: 0,
    duration: 0,
    vehicleType: 'CAR',
    paymentMethod: 'CASH',
    paymentStatus: 'CANCELLED',
    requestedAt: '2025-02-19T08:00:00Z',
    acceptedAt: '2025-02-19T08:05:00Z',
    cancelledAt: '2025-02-19T08:12:00Z',
    city: 'New York'
  },
  {
    tripId: 1005,
    bookingId: 'BK-2025-005',
    riderId: 5,
    riderName: 'Lisa Martinez',
    riderPhone: '+1 555-0105',
    driverId: 8,
    driverName: 'William Garcia',
    driverPhone: '+1 555-0204',
    status: 'COMPLETED',
    pickupLocation: {
      latitude: 40.7282,
      longitude: -73.7949,
      address: 'Queens Center Mall, Queens, NY 11373'
    },
    dropoffLocation: {
      latitude: 40.6892,
      longitude: -73.9442,
      address: 'Brooklyn Bridge Park, Brooklyn, NY 11201'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 22.00,
      timeFare: 7.50,
      subtotal: 34.50,
      surgeMultiplier: 1.5,
      discount: 5.00,
      tax: 2.50,
      platformFee: 2.00,
      totalFare: 34.00
    },
    distance: 12.5,
    duration: 35,
    vehicleType: 'SUV',
    paymentMethod: 'CARD',
    paymentStatus: 'PAID',
    requestedAt: '2025-02-19T07:00:00Z',
    acceptedAt: '2025-02-19T07:02:00Z',
    arrivedAt: '2025-02-19T07:15:00Z',
    startedAt: '2025-02-19T07:18:00Z',
    completedAt: '2025-02-19T07:53:00Z',
    rating: 4,
    city: 'New York'
  },
  {
    tripId: 1006,
    bookingId: 'BK-2025-006',
    riderId: 6,
    riderName: 'Christopher Lee',
    riderPhone: '+1 555-0106',
    driverId: 9,
    driverName: 'Richard Wilson',
    driverPhone: '+1 555-0205',
    status: 'ARRIVED',
    pickupLocation: {
      latitude: 40.7505,
      longitude: -73.9934,
      address: 'Penn Station, New York, NY 10001'
    },
    dropoffLocation: {
      latitude: 40.7489,
      longitude: -73.9680,
      address: 'United Nations HQ, New York, NY 10017'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 4.50,
      timeFare: 1.50,
      subtotal: 11.00,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 0.85,
      platformFee: 0.60,
      totalFare: 12.45
    },
    distance: 1.8,
    duration: 8,
    vehicleType: 'CAR',
    paymentMethod: 'WALLET',
    paymentStatus: 'PENDING',
    requestedAt: '2025-02-19T10:00:00Z',
    acceptedAt: '2025-02-19T10:02:00Z',
    arrivedAt: '2025-02-19T10:08:00Z',
    city: 'New York'
  },
  {
    tripId: 1007,
    bookingId: 'BK-2025-007',
    riderId: 7,
    riderName: 'Amanda White',
    riderPhone: '+1 555-0107',
    driverId: 10,
    driverName: 'Thomas Moore',
    driverPhone: '+1 555-0206',
    status: 'COMPLETED',
    pickupLocation: {
      latitude: 40.7580,
      longitude: -73.9855,
      address: 'Times Square, New York, NY 10036'
    },
    dropoffLocation: {
      latitude: 40.7484,
      longitude: -73.9857,
      address: 'Empire State Building, New York, NY 10118'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 3.25,
      timeFare: 1.25,
      subtotal: 9.50,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 0.75,
      platformFee: 0.50,
      totalFare: 10.75
    },
    distance: 0.6,
    duration: 5,
    vehicleType: 'CAR',
    paymentMethod: 'CARD',
    paymentStatus: 'PAID',
    requestedAt: '2025-02-19T11:30:00Z',
    acceptedAt: '2025-02-19T11:31:00Z',
    arrivedAt: '2025-02-19T11:35:00Z',
    startedAt: '2025-02-19T11:36:00Z',
    completedAt: '2025-02-19T11:41:00Z',
    rating: 5,
    city: 'New York'
  },
  {
    tripId: 1008,
    bookingId: 'BK-2025-008',
    riderId: 8,
    riderName: 'Daniel Harris',
    riderPhone: '+1 555-0108',
    status: 'PENDING',
    pickupLocation: {
      latitude: 40.7739,
      longitude: -73.9442,
      address: 'Upper East Side, New York, NY 10021'
    },
    dropoffLocation: {
      latitude: 40.7505,
      longitude: -73.9934,
      address: 'Midtown Manhattan, New York, NY 10001'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 6.75,
      timeFare: 2.25,
      subtotal: 14.00,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 1.05,
      platformFee: 0.75,
      totalFare: 15.80
    },
    distance: 2.8,
    duration: 10,
    vehicleType: 'CAR',
    paymentMethod: 'CASH',
    paymentStatus: 'PENDING',
    requestedAt: '2025-02-19T12:00:00Z',
    city: 'New York'
  },
  {
    tripId: 1009,
    bookingId: 'BK-2025-009',
    riderId: 9,
    riderName: 'Jennifer Clark',
    riderPhone: '+1 555-0109',
    driverId: 11,
    driverName: 'Charles Martinez',
    driverPhone: '+1 555-0207',
    status: 'ACCEPTED',
    pickupLocation: {
      latitude: 40.6892,
      longitude: -74.0445,
      address: 'Financial District, New York, NY 10005'
    },
    dropoffLocation: {
      latitude: 40.7282,
      longitude: -73.7949,
      address: 'JFK Airport, Queens, NY 11430'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 35.00,
      timeFare: 12.00,
      subtotal: 52.00,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 4.00,
      platformFee: 3.00,
      totalFare: 59.00
    },
    distance: 20.5,
    duration: 55,
    vehicleType: 'SUV',
    paymentMethod: 'CARD',
    paymentStatus: 'PENDING',
    requestedAt: '2025-02-19T12:30:00Z',
    acceptedAt: '2025-02-19T12:32:00Z',
    city: 'New York'
  },
  {
    tripId: 1010,
    bookingId: 'BK-2025-010',
    riderId: 10,
    riderName: 'Matthew Lewis',
    riderPhone: '+1 555-0110',
    driverId: 12,
    driverName: 'Joseph Thompson',
    driverPhone: '+1 555-0208',
    status: 'COMPLETED',
    pickupLocation: {
      latitude: 40.7589,
      longitude: -73.9851,
      address: 'Central Park South, New York, NY 10019'
    },
    dropoffLocation: {
      latitude: 40.7505,
      longitude: -73.9934,
      address: 'Madison Square Garden, New York, NY 10001'
    },
    fare: {
      baseFare: 5.00,
      distanceFare: 5.50,
      timeFare: 1.75,
      subtotal: 12.25,
      surgeMultiplier: 1.0,
      discount: 0,
      tax: 0.95,
      platformFee: 0.65,
      totalFare: 13.85
    },
    distance: 1.2,
    duration: 7,
    vehicleType: 'CAR',
    paymentMethod: 'WALLET',
    paymentStatus: 'PAID',
    requestedAt: '2025-02-19T13:00:00Z',
    acceptedAt: '2025-02-19T13:01:00Z',
    arrivedAt: '2025-02-19T13:06:00Z',
    startedAt: '2025-02-19T13:07:00Z',
    completedAt: '2025-02-19T13:14:00Z',
    rating: 5,
    feedback: 'Quick and efficient!',
    city: 'New York'
  }
];

const MOCK_TOP_ROUTES: TopRoute[] = [
  { routeId: 'R1', routeName: 'Manhattan to JFK Airport', pickupZone: 'Midtown Manhattan', dropoffZone: 'JFK Airport', tripCount: 1250, totalRevenue: 78500, averageFare: 62.80, averageDistance: 18.5 },
  { routeId: 'R2', routeName: 'Financial District to Midtown', pickupZone: 'Financial District', dropoffZone: 'Midtown Manhattan', tripCount: 980, totalRevenue: 24500, averageFare: 25.00, averageDistance: 4.2 },
  { routeId: 'R3', routeName: 'Upper East Side to Times Square', pickupZone: 'Upper East Side', dropoffZone: 'Times Square', tripCount: 850, totalRevenue: 18700, averageFare: 22.00, averageDistance: 2.8 },
  { routeId: 'R4', routeName: 'Brooklyn to Manhattan', pickupZone: 'Downtown Brooklyn', dropoffZone: 'Midtown Manhattan', tripCount: 720, totalRevenue: 36000, averageFare: 50.00, averageDistance: 12.5 },
  { routeId: 'R5', routeName: 'Queens to LaGuardia', pickupZone: 'Queens', dropoffZone: 'LGA Airport', tripCount: 650, totalRevenue: 22750, averageFare: 35.00, averageDistance: 8.0 },
];

const MOCK_CITY_PERFORMANCE: CityPerformance[] = [
  { cityId: 'C1', cityName: 'New York', totalTrips: 8540, completedTrips: 7800, cancelledTrips: 520, noShowTrips: 220, totalRevenue: 425000, averageFare: 54.50, activeDrivers: 1250, averageWaitTime: 4.5, growthRate: 12.5 },
  { cityId: 'C2', cityName: 'Los Angeles', totalTrips: 6200, completedTrips: 5800, cancelledTrips: 280, noShowTrips: 120, totalRevenue: 310000, averageFare: 53.45, activeDrivers: 890, averageWaitTime: 5.2, growthRate: 8.3 },
  { cityId: 'C3', cityName: 'Chicago', totalTrips: 4800, completedTrips: 4450, cancelledTrips: 220, noShowTrips: 130, totalRevenue: 216000, averageFare: 48.50, activeDrivers: 650, averageWaitTime: 4.8, growthRate: 6.7 },
  { cityId: 'C4', cityName: 'Houston', totalTrips: 3200, completedTrips: 2980, cancelledTrips: 150, noShowTrips: 70, totalRevenue: 134400, averageFare: 45.10, activeDrivers: 420, averageWaitTime: 6.1, growthRate: 15.2 },
  { cityId: 'C5', cityName: 'Phoenix', totalTrips: 2100, completedTrips: 1950, cancelledTrips: 100, noShowTrips: 50, totalRevenue: 78000, averageFare: 40.00, activeDrivers: 280, averageWaitTime: 5.5, growthRate: 18.5 },
];

const MOCK_DRIVER_PERFORMANCE: DriverPerformance[] = [
  { driverId: 5, driverName: 'Michael Johnson', totalTrips: 450, completedTrips: 435, cancelledTrips: 15, totalEarnings: 28500, averageRating: 4.85, acceptanceRate: 94.5, onlineHours: 520 },
  { driverId: 6, driverName: 'David Brown', totalTrips: 380, completedTrips: 370, cancelledTrips: 10, totalEarnings: 24100, averageRating: 4.72, acceptanceRate: 91.2, onlineHours: 480 },
  { driverId: 7, driverName: 'James Anderson', totalTrips: 320, completedTrips: 305, cancelledTrips: 15, totalEarnings: 19800, averageRating: 4.68, acceptanceRate: 89.5, onlineHours: 420 },
  { driverId: 8, driverName: 'William Garcia', totalTrips: 410, completedTrips: 398, cancelledTrips: 12, totalEarnings: 26200, averageRating: 4.91, acceptanceRate: 95.8, onlineHours: 510 },
  { driverId: 9, driverName: 'Richard Wilson', totalTrips: 290, completedTrips: 280, cancelledTrips: 10, totalEarnings: 17500, averageRating: 4.55, acceptanceRate: 87.3, onlineHours: 380 },
];

const MOCK_CANCELLATION_REASONS: CancellationReason[] = [
  { reason: 'LONG_WAIT_TIME', count: 245, percentage: 35.2, cancelledBy: 'RIDER' },
  { reason: 'DRIVER_NOT_MOVING', count: 120, percentage: 17.2, cancelledBy: 'RIDER' },
  { reason: 'CHANGED_PLANS', count: 98, percentage: 14.1, cancelledBy: 'RIDER' },
  { reason: 'ALTERNATIVE_TRANSPORT', count: 85, percentage: 12.2, cancelledBy: 'RIDER' },
  { reason: 'VEHICLE_ISSUE', count: 65, percentage: 9.3, cancelledBy: 'DRIVER' },
  { reason: 'TRAFFIC_DELAY', count: 45, percentage: 6.5, cancelledBy: 'DRIVER' },
  { reason: 'RIDER_NO_SHOW', count: 25, percentage: 3.6, cancelledBy: 'DRIVER' },
  { reason: 'SYSTEM_ERROR', count: 12, percentage: 1.7, cancelledBy: 'SYSTEM' },
];

// ============================================================================
// Helper Functions
// ============================================================================

function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

// ============================================================================
// Trip List & Detail Hooks (with MOCK DATA)
// ============================================================================

export function useTrips(
  params: TripFilterParams & { pageable: Pageable },
  options: {
    enabled?: boolean;
    onSuccess?: (data: TripsListResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const page = params.pageable?.page ?? 0;
  const size = params.pageable?.size ?? 10;

  const queryFn = useCallback(async () => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    let filteredTrips = [...MOCK_TRIPS];

    // Apply status filter
    if (params.status) {
      filteredTrips = filteredTrips.filter(t => t.status === params.status);
    }

    // Apply search filter
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filteredTrips = filteredTrips.filter(t => 
        (t.riderName?.toLowerCase().includes(searchLower)) ||
        (t.driverName?.toLowerCase().includes(searchLower)) ||
        (t.bookingId?.toLowerCase().includes(searchLower)) ||
        String(t.tripId).includes(searchLower)
      );
    }

    // Apply rider filter
    if (params.riderId) {
      filteredTrips = filteredTrips.filter(t => t.riderId === Number(params.riderId));
    }

    // Apply driver filter
    if (params.driverId) {
      filteredTrips = filteredTrips.filter(t => t.driverId === Number(params.driverId));
    }

    // Sort by requestedAt desc
    filteredTrips.sort((a, b) => 
      new Date(b.requestedAt || 0).getTime() - new Date(a.requestedAt || 0).getTime()
    );

    const totalElements = filteredTrips.length;
    const totalPages = Math.ceil(totalElements / size);
    const startIndex = page * size;
    const paginatedTrips = filteredTrips.slice(startIndex, startIndex + size);

    // Return PagedData directly (not wrapped in ApiResponse) to match component expectations
    const mockResponse: PagedData<Trip> = {
      content: paginatedTrips,
      page: {
        size,
        number: page,
        totalElements,
        totalPages
      },
      first: page === 0,
      last: page >= totalPages - 1,
      empty: paginatedTrips.length === 0
    };

    return mockResponse as TripsListResponse;
  }, [page, size, params.status, params.search, params.riderId, params.driverId]);

  return useFetch<TripsListResponse>(null as any, {
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

export function useTrip(
  tripId: number | string | null,
  options: {
    enabled?: boolean;
    onSuccess?: (data: TripResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    if (!tripId) throw new Error('Trip ID is required');
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const trip = MOCK_TRIPS.find(t => t.tripId === Number(tripId));
    
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    // Return Trip directly (not wrapped in ApiResponse)
    return trip as TripResponse;
  }, [tripId]);

  return useFetch<TripResponse>(null as any, {
    enabled: options.enabled !== false && !!tripId,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

// ============================================================================
// Trip Location & Replay Hooks (with MOCK DATA)
// ============================================================================

export function useTripLocations(
  tripId: number | string | null,
  options: {
    enabled?: boolean;
    onSuccess?: (data: TripLocationsResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    if (!tripId) throw new Error('Trip ID is required');
    
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const trip = MOCK_TRIPS.find(t => t.tripId === Number(tripId));
    
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    // Generate mock location history
    const locations: TripLocation[] = [];
    const pickup = trip.pickupLocation;
    const dropoff = trip.dropoffLocation;
    
    if (pickup && dropoff) {
      const steps = 10;
      for (let i = 0; i <= steps; i++) {
        const ratio = i / steps;
        locations.push({
          latitude: pickup.latitude + (dropoff.latitude - pickup.latitude) * ratio,
          longitude: pickup.longitude + (dropoff.longitude - pickup.longitude) * ratio,
          timestamp: new Date(Date.now() - (steps - i) * 60000).toISOString(),
          heading: 45 + Math.random() * 10,
          speed: 25 + Math.random() * 15,
          accuracy: 5 + Math.random() * 3
        });
      }
    }

    // Return locations directly (not wrapped in ApiResponse)
    return locations as TripLocationsResponse;
  }, [tripId]);

  return useFetch<TripLocationsResponse>(null as any, {
    enabled: options.enabled !== false && !!tripId,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

export function useTripReplay(
  tripId: number | string | null,
  options: {
    enabled?: boolean;
    onSuccess?: (data: TripReplayResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    if (!tripId) throw new Error('Trip ID is required');
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const trip = MOCK_TRIPS.find(t => t.tripId === Number(tripId));
    
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    // Generate mock replay points
    const points: TripReplayPoint[] = [];
    const pickup = trip.pickupLocation;
    const dropoff = trip.dropoffLocation;
    
    if (pickup && dropoff) {
      const steps = 20;
      const events: TripReplayPoint['event'][] = ['PICKUP', 'EN_ROUTE', 'EN_ROUTE', 'EN_ROUTE', 'DROPOFF'];
      
      for (let i = 0; i <= steps; i++) {
        const ratio = i / steps;
        points.push({
          latitude: pickup.latitude + (dropoff.latitude - pickup.latitude) * ratio,
          longitude: pickup.longitude + (dropoff.longitude - pickup.longitude) * ratio,
          timestamp: new Date(Date.now() - (steps - i) * 120000).toISOString(),
          speed: 20 + Math.random() * 20,
          heading: 45 + Math.random() * 15,
          event: i === 0 ? 'PICKUP' : i === steps ? 'DROPOFF' : 'EN_ROUTE'
        });
      }
    }

    // Return replay data directly (not wrapped in ApiResponse)
    return {
      tripId: Number(tripId),
      points,
      totalDuration: trip.duration || 0,
      totalDistance: trip.distance || 0
    } as TripReplayResponse;
  }, [tripId]);

  return useFetch<TripReplayResponse>(null as any, {
    enabled: options.enabled !== false && !!tripId,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

// ============================================================================
// Trip Management Mutations (with MOCK DATA)
// ============================================================================

export function useForceEndTrip(
  options: {
    onSuccess?: (data: TripResponse, variables: { tripId: number | string; reason?: string }) => void;
    onError?: (error: Error, variables: { tripId: number | string; reason?: string }) => void;
  } = {}
) {
  return useMutation<TripResponse, { tripId: number | string; reason?: string }>(
    async ({ tripId, reason }) => {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const tripIndex = MOCK_TRIPS.findIndex(t => t.tripId === Number(tripId));
      
      if (tripIndex === -1) {
        throw new Error(`Trip ${tripId} not found`);
      }

      // Update the mock trip status
      MOCK_TRIPS[tripIndex] = {
        ...MOCK_TRIPS[tripIndex],
        status: 'FORCE_ENDED',
        completedAt: new Date().toISOString()
      };

      // Return updated trip directly (not wrapped in ApiResponse)
      return MOCK_TRIPS[tripIndex] as TripResponse;
    },
    options
  );
}

export function useAssignDriver(
  options: {
    onSuccess?: (data: TripResponse, variables: { tripId: number | string; driverId: number }) => void;
    onError?: (error: Error, variables: { tripId: number | string; driverId: number }) => void;
  } = {}
) {
  return useMutation<TripResponse, { tripId: number | string; driverId: number }>(
    async ({ tripId, driverId }) => {
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const tripIndex = MOCK_TRIPS.findIndex(t => t.tripId === Number(tripId));
      
      if (tripIndex === -1) {
        throw new Error(`Trip ${tripId} not found`);
      }

      // Mock driver data
      const mockDrivers: Record<number, { name: string; phone: string }> = {
        5: { name: 'Michael Johnson', phone: '+1 555-0201' },
        6: { name: 'David Brown', phone: '+1 555-0202' },
        7: { name: 'James Anderson', phone: '+1 555-0203' },
        8: { name: 'William Garcia', phone: '+1 555-0204' },
        9: { name: 'Richard Wilson', phone: '+1 555-0205' },
        10: { name: 'Thomas Moore', phone: '+1 555-0206' },
      };

      const driver = mockDrivers[driverId] || { name: 'Unknown Driver', phone: '+1 555-0000' };

      // Update the mock trip
      MOCK_TRIPS[tripIndex] = {
        ...MOCK_TRIPS[tripIndex],
        driverId,
        driverName: driver.name,
        driverPhone: driver.phone,
        status: 'ASSIGNED',
        acceptedAt: new Date().toISOString()
      };

      // Return updated trip directly (not wrapped in ApiResponse)
      return MOCK_TRIPS[tripIndex] as TripResponse;
    },
    options
  );
}

// ============================================================================
// Trip History Hook (with MOCK DATA)
// ============================================================================

export function useTripHistory(
  userType: 'rider' | 'driver',
  userId: number | string | null,
  params: { pageable: Pageable } = { pageable: { page: 0, size: 10 } },
  options: {
    enabled?: boolean;
    onSuccess?: (data: TripHistoryResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const page = params.pageable?.page ?? 0;
  const size = params.pageable?.size ?? 10;

  const queryFn = useCallback(async () => {
    if (!userId) throw new Error('User ID is required');
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    let filteredTrips = MOCK_TRIPS.filter(t => 
      userType === 'rider' ? t.riderId === Number(userId) : t.driverId === Number(userId)
    );

    // Sort by requestedAt desc
    filteredTrips.sort((a, b) => 
      new Date(b.requestedAt || 0).getTime() - new Date(a.requestedAt || 0).getTime()
    );

    const totalElements = filteredTrips.length;
    const totalPages = Math.ceil(totalElements / size);
    const startIndex = page * size;
    const paginatedTrips = filteredTrips.slice(startIndex, startIndex + size);

    // Return PagedData directly (not wrapped in ApiResponse)
    return {
      content: paginatedTrips,
      page: {
        size,
        number: page,
        totalElements,
        totalPages
      },
      first: page === 0,
      last: page >= totalPages - 1,
      empty: paginatedTrips.length === 0
    } as TripHistoryResponse;
  }, [userType, userId, page, size]);

  return useFetch<TripHistoryResponse>(null as any, {
    enabled: options.enabled !== false && !!userId,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

// ============================================================================
// Export Mutations (MOCK - generates dummy file)
// ============================================================================

function safeExportFilename(from?: string, to?: string, ext: string = 'csv'): string {
  const sanitize = (s: string) => s.replace(/[:/\\?*"<>|]/g, '-').replace(/\s+/g, '-');
  const fromPart = from ? sanitize(from) : 'all';
  const toPart = to ? sanitize(to) : 'all';
  return `trips-${fromPart}-${toPart}.${ext}`;
}

export function useExportTripsCsv(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, ExportTripsParams>(
    async (params) => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate mock CSV content
      const headers = 'Trip ID,Booking ID,Rider Name,Driver Name,Status,Pickup,Dropoff,Fare,Date\n';
      const rows = MOCK_TRIPS.map(t => 
        `${t.tripId},${t.bookingId},${t.riderName},${t.driverName || 'N/A'},${t.status},${t.pickupLocation?.address || 'N/A'},${t.dropoffLocation?.address || 'N/A'},${t.fare?.totalFare || 0},${t.requestedAt}`
      ).join('\n');
      
      const csvContent = headers + rows;
      return new Blob([csvContent], { type: 'text/csv' });
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeExportFilename(params.from, params.to, 'csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: (error, params) => {
        options.onError?.(error);
      },
    }
  );
}

export function useExportTripsPdf(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, ExportTripsParams>(
    async (params) => {
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Generate mock PDF content (just a text file for demo)
      const content = `TRIPS REPORT\n\nGenerated: ${new Date().toISOString()}\n\n` +
        MOCK_TRIPS.map(t => 
          `Trip #${t.tripId} - ${t.riderName} - ${t.status} - $${t.fare?.totalFare || 0}`
        ).join('\n');
      
      return new Blob([content], { type: 'application/pdf' });
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeExportFilename(params.from, params.to, 'pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: (error, params) => {
        options.onError?.(error);
      },
    }
  );
}

// ============================================================================
// Receipt & Communication Mutations (MOCK)
// ============================================================================

export function useSendTripReceipt(
  options: {
    onSuccess?: (data: { success: boolean; message: string }, variables: { tripId: number | string; email?: string }) => void;
    onError?: (error: Error, variables: { tripId: number | string; email?: string }) => void;
  } = {}
) {
  return useMutation<{ success: boolean; message: string }, { tripId: number | string; email?: string }>(
    async ({ tripId, email }) => {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      return {
        success: true,
        message: `Receipt for trip ${tripId} sent successfully to ${email || 'rider email'}`
      };
    },
    options
  );
}

// ============================================================================
// Analytics & Reports Hooks (with MOCK DATA)
// ============================================================================

export function useTopRoutes(
  params: { from?: string; to?: string; limit?: number } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: TopRoutesResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Return data directly (not wrapped in ApiResponse)
    return MOCK_TOP_ROUTES as TopRoutesResponse;
  }, []);

  return useFetch<TopRoutesResponse>(null as any, {
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

export function useCityPerformance(
  params: { from?: string; to?: string } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: CityPerformanceResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Return data directly (not wrapped in ApiResponse)
    return MOCK_CITY_PERFORMANCE as CityPerformanceResponse;
  }, []);

  return useFetch<CityPerformanceResponse>(null as any, {
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

export function useDriverPerformance(
  params: { from?: string; to?: string; driverId?: number | string } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverPerformanceResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    let data = MOCK_DRIVER_PERFORMANCE;
    
    if (params.driverId) {
      data = data.filter(d => d.driverId === Number(params.driverId));
    }

    // Return data directly (not wrapped in ApiResponse)
    return data as DriverPerformanceResponse;
  }, [params.driverId]);

  return useFetch<DriverPerformanceResponse>(null as any, {
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

export function useCancellationReasons(
  params: { from?: string; to?: string } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: CancellationReasonsResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return data directly (not wrapped in ApiResponse)
    return MOCK_CANCELLATION_REASONS as CancellationReasonsResponse;
  }, []);

  return useFetch<CancellationReasonsResponse>(null as any, {
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    queryFn,
  });
}

// ============================================================================
// Analytics Export Mutations (MOCK)
// ============================================================================

function safeAnalyticsExportFilename(type: string, from?: string, to?: string, ext: string = 'csv'): string {
  const sanitize = (s: string) => s.replace(/[:/\\?*"<>|]/g, '-').replace(/\s+/g, '-');
  const fromPart = from ? sanitize(from) : 'all';
  const toPart = to ? sanitize(to) : 'all';
  return `${type}-${fromPart}-${toPart}.${ext}`;
}

export function useExportTopRoutesCsv(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, { from?: string; to?: string }>(
    async (params) => {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const headers = 'Route ID,Route Name,Pickup Zone,Dropoff Zone,Trip Count,Revenue,Avg Fare,Avg Distance\n';
      const rows = MOCK_TOP_ROUTES.map(r => 
        `${r.routeId},${r.routeName},${r.pickupZone},${r.dropoffZone},${r.tripCount},${r.totalRevenue},${r.averageFare},${r.averageDistance}`
      ).join('\n');
      
      return new Blob([headers + rows], { type: 'text/csv' });
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeAnalyticsExportFilename('top-routes', params.from, params.to, 'csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: options.onError,
    }
  );
}

export function useExportCityPerformanceCsv(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, { from?: string; to?: string }>(
    async (params) => {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const headers = 'City ID,City,Total Trips,Completed,Cancelled,Revenue,Avg Fare,Active Drivers,Growth Rate\n';
      const rows = MOCK_CITY_PERFORMANCE.map(c => 
        `${c.cityId},${c.cityName},${c.totalTrips},${c.completedTrips},${c.cancelledTrips},${c.totalRevenue},${c.averageFare},${c.activeDrivers},${c.growthRate}`
      ).join('\n');
      
      return new Blob([headers + rows], { type: 'text/csv' });
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeAnalyticsExportFilename('city-performance', params.from, params.to, 'csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: options.onError,
    }
  );
}

export function useExportDriverPerformanceCsv(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, { from?: string; to?: string }>(
    async (params) => {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const headers = 'Driver ID,Driver Name,Total Trips,Completed,Earnings,Rating,Acceptance Rate,Online Hours\n';
      const rows = MOCK_DRIVER_PERFORMANCE.map(d => 
        `${d.driverId},${d.driverName},${d.totalTrips},${d.completedTrips},${d.totalEarnings},${d.averageRating},${d.acceptanceRate},${d.onlineHours}`
      ).join('\n');
      
      return new Blob([headers + rows], { type: 'text/csv' });
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeAnalyticsExportFilename('driver-performance', params.from, params.to, 'csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: options.onError,
    }
  );
}

export function useExportCancellationReasonsCsv(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, { from?: string; to?: string }>(
    async (params) => {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const headers = 'Reason,Count,Percentage,Cancelled By\n';
      const rows = MOCK_CANCELLATION_REASONS.map(c => 
        `${c.reason},${c.count},${c.percentage},${c.cancelledBy}`
      ).join('\n');
      
      return new Blob([headers + rows], { type: 'text/csv' });
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeAnalyticsExportFilename('cancellation-reasons', params.from, params.to, 'csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: options.onError,
    }
  );
}
